import { watch } from 'vue'
import { useCookieConsent } from '~/composables/useCookieConsent'

export default defineNuxtPlugin(() => {
  if (typeof window === 'undefined') return

  const route = useRoute()
  const { getConsent } = useCookieConsent()
  const MAX_READY_POLL_ATTEMPTS = 50
  let readyDispatched = false
  let readyPollId: number | null = null
  let readyPollAttempts = 0

  const shouldBridgeSignalsKit = (path: string) => {
    const isAppRoute = path.startsWith('/app/') || path.startsWith('/fr/app/') || path === '/app' || path === '/fr/app'
    return !isAppRoute
  }

  const getTracker = () => {
    const tracker = (window as any).signalsKit
    if (!tracker || typeof tracker.track !== 'function') {
      return null
    }

    return tracker
  }

  const stopReadyPoll = () => {
    if (readyPollId !== null) {
      window.clearInterval(readyPollId)
      readyPollId = null
    }

    readyPollAttempts = 0
  }

  const syncSignalsConsent = (granted: boolean) => {
    const tracker = getTracker()
    if (!tracker || typeof tracker.setConsent !== 'function') {
      return
    }

    try {
      tracker.setConsent(granted)
    } catch (error) {
      console.warn('[SignalsKit] Error syncing consent:', error)
    }
  }

  const markSignalsReady = () => {
    const tracker = getTracker()
    if (!tracker) {
      return false
    }

    syncSignalsConsent(getConsent().analytics === true)

    if (!readyDispatched) {
      readyDispatched = true
      window.dispatchEvent(new Event('signalskit:ready'))
    }

    stopReadyPoll()
    return true
  }

  const waitForSignalsKit = () => {
    const currentPath = route?.path || window.location.pathname
    if (!shouldBridgeSignalsKit(currentPath) || markSignalsReady() || readyPollId !== null) {
      return
    }

    readyPollId = window.setInterval(() => {
      readyPollAttempts += 1

      if (markSignalsReady() || readyPollAttempts >= MAX_READY_POLL_ATTEMPTS) {
        stopReadyPoll()
      }
    }, 300)
  }

  watch(
    () => getConsent().analytics,
    (granted) => {
      if (granted === true) {
        waitForSignalsKit()
      }

      syncSignalsConsent(granted === true)
    },
    { immediate: true }
  )

  watch(
    () => route.path,
    (path) => {
      if (!shouldBridgeSignalsKit(path)) {
        stopReadyPoll()
        return
      }

      if (getConsent().analytics === true || getTracker()) {
        waitForSignalsKit()
      }
    },
    { immediate: true }
  )
})
